import { useEffect } from 'react';
import { skip } from 'rxjs';
import { COMMON } from '../configs/main.config';
import { BusEvent, EBusEvent } from '../services/bus-event/bus-event.service';
import { Dynamics } from '../services/dynamics/dynamics';
import { DynamicsV2 } from '../services/dynamics/dynamics-v2';
import { EChannelProvider } from '../services/solgari-api/enums/channel-provider.enum';
import { clearIdentity } from '../shared/helpers/clear-identity.helper';
import { IMsgNotification } from '../shared/helpers/incoming-messages';
import { saveChatNoteEntity } from '../shared/helpers/save-chat-note.helper';
import { useStore } from '../stores';

export const useChats = ({ dynVersion, navigateTo }) => {
  const {
    chatsStore: { inboxChats, updateChat, addChat },
    settingsStore: { notification: storeNotification },
  } = useStore();
  useEffect(() => {
    const sub$ = BusEvent.getBus$()
      .pipe(skip(1))
      .subscribe((data) => {
        if (data?.event === EBusEvent.NEW_CHAT_MESSAGE) {
          const message = data.payload as IMsgNotification;
          openSessionForChat(
            message.chatId,
            message.source as EChannelProvider,
            message.clientPhoneNumber,
          );
        }
      });

    return () => sub$.unsubscribe();
  }, [inboxChats, storeNotification?.incomingMessage]);

  const showNotification = async () => {
    const permission = Notification?.permission;

    if (permission === 'granted' && storeNotification.incomingMessage) {
      const options = {
        body: 'Your have a new incoming message',
        silent: false,
      };
      const notice = new Notification('New message', options);
      notice.onclick = () => {
        navigateTo('/inbox', { state: { from: location?.pathname } });
      };
    }
  };

  const updateChatByNewMessage = async (
    chatId: string,
    source: EChannelProvider,
    identity: string,
  ) => {
    const foundChat = inboxChats.find((chat) => chat.chatId === chatId);

    if (foundChat) {
      await updateChat(foundChat);
      return foundChat;
    }

    const cleanIdentity = clearIdentity(source, identity);
    const user =
      (await Dynamics.searchEntities(cleanIdentity, true, true))?.[0] ??
      COMMON.UNKNOWN_ENTITY;

    await saveChatNoteEntity(
      chatId,
      identity,
      source as EChannelProvider,
      user,
    );
    const chatNote = (await Dynamics.searchChatNotes({ id: chatId }))?.[0];
    const chatEntity = await addChat({ chatId, user, chatNote });

    return chatEntity;
  };

  const openSessionForChat = async (
    chatId: string,
    source: EChannelProvider,
    identity: string,
  ) => {
    const chatEntity = await updateChatByNewMessage(chatId, source, identity);

    if (dynVersion === 1) {
      showNotification();
      return;
    }

    const session = DynamicsV2.getSessionByConnectedId(chatId);

    if (!session) {
      const result = await DynamicsV2.createNotification(
        `${source} from ${identity}`,
      );

      if (!result || result.actionName === 'Reject') {
        return;
      }

      const { chatNote, user } = chatEntity;

      if (result.actionName === 'Accept') {
        DynamicsV2.createChatSession(
          chatId,
          chatNote?.id,
          {
            source,
            user,
            identity,
          },
          () =>
            navigateTo(`/inbox/${chatId}`, {
              state: { skipOpening: true, from: location?.pathname },
            }),
        );
      }

      return;
    }

    if (!chatId && session) {
      return;
    }

    if (session.focused) {
      return;
    }

    DynamicsV2.focusSession(session.sessionId);
  };

  return {
    openSessionForChat,
  };
};
